<template>
  <aside
    class="bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col transition-all duration-300"
    :class="collapsed ? 'w-16' : 'w-64'"
  >
    <!-- Logo -->
    <div class="h-16 flex items-center justify-between px-4 border-b border-gray-200 dark:border-gray-700">
      <div v-if="!collapsed" class="flex items-center gap-2">
        <img v-if="appStore.appLogo" :src="appStore.appLogo" alt="Logo" class="w-8 h-8 rounded-lg object-cover" />
        <span class="text-lg font-bold text-primary-600 dark:text-primary-400 truncate">
          {{ appStore.appName }}
        </span>
      </div>
      <img v-else :src="appStore.appLogo || ''" alt="" class="w-8 h-8 rounded-lg object-cover" v-show="appStore.appLogo" />
      <button
        @click="$emit('toggle')"
        class="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
      >
        <ChevronLeft v-if="!collapsed" class="w-5 h-5" />
        <ChevronRight v-else class="w-5 h-5" />
      </button>
    </div>

    <!-- Navigation -->
    <nav class="flex-1 overflow-y-auto py-4">
      <ul class="space-y-1 px-2">
        <li v-for="item in menuItems" :key="item.name">
          <!-- Simple link -->
          <router-link
            v-if="!item.children"
            :to="item.to"
            class="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
            :class="isActive(item.to) ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'"
          >
            <component :is="item.icon" class="w-5 h-5 flex-shrink-0" />
            <span v-if="!collapsed">{{ item.label }}</span>
          </router-link>

          <!-- Group with children -->
          <div v-else>
            <button
              @click="toggleGroup(item.name)"
              class="flex items-center justify-between w-full px-3 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <div class="flex items-center gap-3">
                <component :is="item.icon" class="w-5 h-5 flex-shrink-0" />
                <span v-if="!collapsed">{{ item.label }}</span>
              </div>
              <ChevronDown
                v-if="!collapsed"
                class="w-4 h-4 transition-transform"
                :class="{ 'rotate-180': openGroups[item.name] }"
              />
            </button>
            <ul v-if="!collapsed && openGroups[item.name]" class="mt-1 ml-8 space-y-1">
              <li v-for="child in item.children" :key="child.name">
                <router-link
                  :to="child.to"
                  class="flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors"
                  :class="isActive(child.to) ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'"
                >
                  <component :is="child.icon" class="w-4 h-4 flex-shrink-0" />
                  <span>{{ child.label }}</span>
                </router-link>
              </li>
            </ul>
          </div>
        </li>
      </ul>
    </nav>
  </aside>
</template>

<script setup>
import { reactive } from 'vue'
import { useRoute } from 'vue-router'
import { useAppStore } from '@/stores/app'
import {
  LayoutDashboard,
  Building2,
  CreditCard,
  Languages,
  FolderTree,
  HeadphonesIcon,
  Users,
  Star,
  AlertTriangle,
  Server,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Package,
  FileText,
  Info,
  Scale,
  Shield,
  Contact,
  Zap,
  Bell,
  Activity,
  Globe,
  MapPin,
  SlidersHorizontal,
  Megaphone,
} from 'lucide-vue-next'

defineProps({
  collapsed: Boolean,
})
defineEmits(['toggle'])

const route = useRoute()
const appStore = useAppStore()

const openGroups = reactive({})

function toggleGroup(name) {
  openGroups[name] = !openGroups[name]
}

function isActive(path) {
  return route.path === path || route.path.startsWith(path + '/')
}

const menuItems = [
  {
    name: 'Dashboard',
    label: 'Dashboard',
    to: '/',
    icon: LayoutDashboard,
  },
  {
    name: 'Tenants',
    label: 'Tenants',
    to: '/tenants',
    icon: Building2,
  },
  {
    name: 'Subscriptions',
    label: 'Subscriptions',
    icon: CreditCard,
    children: [
      { name: 'Plans', label: 'Plans', to: '/subscriptions/plans', icon: Package },
      { name: 'TenantSubs', label: 'Tenant Plans', to: '/subscriptions/tenants', icon: FileText },
    ],
  },
  {
    name: 'Categories',
    label: 'Business Categories',
    icon: FolderTree,
    children: [
      { name: 'CategoriesList', label: 'Categories', to: '/categories', icon: FolderTree },
      { name: 'CategoryAttributes', label: 'Attributes', to: '/categories/attributes', icon: SlidersHorizontal },
    ],
  },
  {
    name: 'Countries',
    label: 'Countries',
    to: '/countries',
    icon: MapPin,
  },
  {
    name: 'LanguagesMenu',
    label: 'Languages & i18n',
    icon: Globe,
    children: [
      { name: 'Languages', label: 'Languages', to: '/languages', icon: Globe },
      { name: 'Translations', label: 'UI Translations', to: '/translations', icon: Languages },
      { name: 'ModelTranslations', label: 'Data Translations', to: '/model-translations', icon: Languages },
    ],
  },
  {
    name: 'Support',
    label: 'Support Tickets',
    to: '/support',
    icon: HeadphonesIcon,
  },
  {
    name: 'Customers',
    label: 'Customers',
    to: '/customers',
    icon: Users,
  },
  {
    name: 'Reviews',
    label: 'Reviews',
    to: '/reviews',
    icon: Star,
  },
  {
    name: 'Complaints',
    label: 'Complaints',
    to: '/complaints',
    icon: AlertTriangle,
  },
  {
    name: 'Servers',
    label: 'Servers',
    icon: Server,
    children: [
      { name: 'ServerList', label: 'Servers List', to: '/servers', icon: Server },
      { name: 'Monitor', label: 'Monitor', to: '/servers/monitor', icon: Activity },
      { name: 'Settings', label: 'Settings', to: '/servers/settings', icon: Settings },
      { name: 'Alerts', label: 'Alert History', to: '/monitoring/alerts', icon: Bell },
    ],
  },
  {
    name: 'Services',
    label: 'App Services',
    to: '/services',
    icon: Zap,
  },
  {
    name: 'SystemOffers',
    label: 'Offers & Banners',
    to: '/system-offers',
    icon: Megaphone,
  },
  {
    name: 'Content',
    label: 'Content',
    icon: FileText,
    children: [
      { name: 'AboutPage', label: 'About Page', to: '/content/about', icon: Info },
      { name: 'Terms', label: 'Terms & Conditions', to: '/content/terms', icon: Scale },
      { name: 'Privacy', label: 'Privacy Policy', to: '/content/privacy', icon: Shield },
      { name: 'FooterContact', label: 'Footer & Contact', to: '/content/footer', icon: Contact },
    ],
  },
]
</script>
